// ============================================================================
// USER SCOPE SERVICE
// ============================================================================
// API calls for managing org data scope for a user

import { get, post, del } from '../client/axiosClient';
import { userScopeEndpoints } from '../endpoints/iam.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  BackendPagedResult,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type { UserScopeDto, UpsertUserScopeRequest } from '../types/iam.types';

class UserScopeService {
  async getByUser(
    userId: ID,
    params?: { page?: number; pageSize?: number }
  ): Promise<PaginatedResponse<UserScopeDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(userScopeEndpoints.list(userId), params)
      : userScopeEndpoints.list(userId);
    const response = await get<ApiResponse<BackendPagedResult<UserScopeDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page,
        pageSize: paged.pageSize,
        totalPages: paged.totalPages,
        totalItems: paged.totalCount,
        hasNextPage: paged.hasNext,
        hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  async create(userId: ID, data: UpsertUserScopeRequest): Promise<ApiResponse<UserScopeDto>> {
    const response = await post<ApiResponse<UserScopeDto>>(
      userScopeEndpoints.create(userId),
      data
    );
    return response.data;
  }

  async delete(userId: ID, id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(userScopeEndpoints.delete(userId, id));
    return response.data;
  }
}

export const userScopeService = new UserScopeService();
export default userScopeService;
